import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Table, Button, Space, Select, Typography, Input, DatePicker, Form, Row, Col, Tag, Popconfirm, App
} from 'antd';
import { DownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import ActionIcon from '../../components/ActionIcon.jsx';
import {
    selectAccountsBySegment,
    selectTransactionsBySegment,
    selectFundPrices,
    selectNavBatches,
    selectGeneratedReports,
    generateReport,
    deleteReport
} from '../../redux/portfolioSlice';
import {
    REQUEST_TYPES,
    REQUEST_TYPE_OPTIONS,
    PERIOD_TYPES,
    PERIOD_TYPE_OPTIONS,
    PERIOD_VALUE_LABELS,
    periodOptions,
    isPerFundType,
    needsRecipient,
    isClientScoped,
    buildDocument,
    describeRequest,
    documentFileName
} from '../../domain/statementReports';
import { toCsv } from '../../domain/reports';
import { downloadCsv } from '../../domain/redemptionDocument';
import { FUNDS } from '../../data/seed';
import { useAccess } from '../../auth/AccessContext.jsx';

const { Text } = Typography;
const ISO = 'YYYY-MM-DD';

/**
 * Statement generation — the production `reqAccountStatement` and `genAccountStatement` steps in one
 * place, since the prototype has no workflow engine to carry a request between them.
 *
 * Fill the request, press Generate, and the document lands in the table below with a download link.
 * A Tax Report produces one document per selected fund, exactly as the production step seeds one
 * upload slot per fund.
 *
 * @param {string} [segment] limits clients and transactions to one desk; omitted means whole book
 */
const ReportsSection = ({ segment, title = 'Statements & reports' }) => {
    const dispatch = useDispatch();
    const { message } = App.useApp();
    const canRun = useAccess().canAct('reports', 'run');
    const [form] = Form.useForm();

    const accounts = useSelector(selectAccountsBySegment(segment));
    const transactions = useSelector(selectTransactionsBySegment(segment));
    const fundPrices = useSelector(selectFundPrices);
    const navBatches = useSelector(selectNavBatches);
    const generated = useSelector(selectGeneratedReports);

    const reportType = Form.useWatch('reportType', form);
    const periodType = Form.useWatch('periodType', form);

    const [expanded, setExpanded] = useState([]);

    const book = useMemo(
        () => ({ accounts, transactions, fundPrices, navBatches }),
        [accounts, transactions, fundPrices, navBatches]
    );

    const concretePeriods = useMemo(() => periodOptions(periodType), [periodType]);

    /** Resolve the period into plain start/end dates, whichever way it was picked. */
    const resolvePeriod = (values) => {
        if (values.periodType === PERIOD_TYPES.CUSTOM) {
            const [from, to] = values.customRange || [];
            return { startDate: from?.format(ISO) || null, endDate: to?.format(ISO) || null };
        }
        const chosen = concretePeriods.find((p) => p.value === values.periodValue);
        return { startDate: chosen?.startDate || null, endDate: chosen?.endDate || null };
    };

    const submit = (values) => {
        const { startDate, endDate } = resolvePeriod(values);

        const base = {
            reportType: values.reportType,
            accountId: isClientScoped(values.reportType) ? values.accountId : null,
            periodType: values.periodType,
            periodLabel:
                values.periodType === PERIOD_TYPES.CUSTOM
                    ? `${startDate || '—'} → ${endDate || '—'}`
                    : concretePeriods.find((p) => p.value === values.periodValue)?.label || '—',
            startDate,
            endDate,
            recipientName: values.recipientName || '',
            recipientOrganization: values.recipientOrganization || '',
            description: values.description || ''
        };

        /*
         * A Tax Report is produced per fund — one document per selected fund, mirroring the
         * per-fund slots the production generate step seeds. Every other type is a single document
         * covering whichever funds were selected.
         */
        const requests = isPerFundType(values.reportType)
            ? (values.fundIds?.length ? values.fundIds : FUNDS.map((f) => f.fundId)).map((fundId) => ({
                  ...base,
                  fundId,
                  fundIds: [fundId]
              }))
            : [{ ...base, fundId: null, fundIds: values.fundIds || [] }];

        requests.forEach((request) => {
            const { columns, rows } = buildDocument(request, book);
            dispatch(
                generateReport({
                    request,
                    columns,
                    rows,
                    fileName: documentFileName(request)
                })
            );
        });

        message.success(
            requests.length === 1
                ? 'Statement generated'
                : `${requests.length} statements generated — one per fund`
        );
    };

    const download = (report) => {
        if (!report.rows.length) return message.warning('This document has no rows to export');
        downloadCsv(report.fileName, toCsv(report.columns, report.rows));
        message.success(`${report.fileName} downloaded`);
        return undefined;
    };

    const generatedColumns = [
        {
            title: 'Documents',
            key: 'fileName',
            render: (_, report) => {
                const { type } = describeRequest(report.request, accounts);
                return (
                    <>
                        {/* The link IS the download — a generated report is only useful as a file. */}
                        <a onClick={() => download(report)}>{report.fileName}</a>
                        <br />
                        <Text type="secondary" style={{ fontSize: 11.5 }}>
                            {type}
                            {report.request.fundId ? ` · ${report.request.fundId}` : ''}
                        </Text>
                    </>
                );
            }
        },
        {
            title: 'Client',
            key: 'client',
            width: 210,
            render: (_, report) => {
                const { scope, segment: desk } = describeRequest(report.request, accounts);
                return (
                    <>
                        <Text>{scope}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 11.5 }}>
                            {desk}
                        </Text>
                    </>
                );
            }
        },
        {
            title: 'Period',
            key: 'period',
            width: 200,
            render: (_, report) => (
                <>
                    <Text>{report.request.periodLabel}</Text>
                    {report.request.startDate && (
                        <>
                            <br />
                            <Text type="secondary" style={{ fontSize: 11.5 }}>
                                {report.request.startDate} → {report.request.endDate}
                            </Text>
                        </>
                    )}
                </>
            )
        },
        { title: 'Rows', dataIndex: 'rowCount', key: 'rowCount', width: 80, align: 'right' },
        {
            title: 'Generated',
            dataIndex: 'generatedAt',
            key: 'generatedAt',
            width: 160,
            render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm')
        },
        {
            title: '',
            key: 'actions',
            width: 200,
            render: (_, report) => (
                <Space className="reports-row-actions">
                    <Button size="small" className="reports-action reports-action-csv" icon={<DownloadOutlined />} onClick={() => download(report)}>
                        CSV
                    </Button>
                    <Popconfirm
                        disabled={!canRun}
                        title="Delete this document?"
                        onConfirm={() => dispatch(deleteReport(report.id))}
                    >
                        <Button size="small" danger className="reports-action reports-action-delete" icon={<DeleteOutlined />} disabled={!canRun}>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <>
            <div className="panel">
                <h3 className="panel-title">{title}</h3>
                <div className="permission-info-banner reports-info-banner">
                    <ActionIcon name="info" />
                    <span>
                        Request and generate in one step - the production workflow splits these across
                        reqAccountStatement and genAccountStatement.
                    </span>
                </div>

                <Form
                    className="reports-form"
                    form={form}
                    layout="vertical"
                    requiredMark={false}
                    initialValues={{
                        reportType: REQUEST_TYPES.ACCOUNT_STATEMENT,
                        periodType: PERIOD_TYPES.TAX_YEAR,
                        accountId: accounts[0]?.accountId
                    }}
                    onFinish={submit}
                >
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item name="reportType" label="Statement type" rules={[{ required: true }]}>
                                <Select
                                    options={REQUEST_TYPE_OPTIONS}
                                    onChange={() => form.setFieldsValue({ fundIds: [] })}
                                />
                            </Form.Item>
                        </Col>

                        {isClientScoped(reportType) && (
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="accountId"
                                    label="Client"
                                    rules={[{ required: true, message: 'Pick a client' }]}
                                >
                                    <Select
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder="Select a client"
                                        options={accounts.map((a) => ({
                                            value: a.accountId,
                                            label: `${a.accountName} — ${a.accountId}`
                                        }))}
                                    />
                                </Form.Item>
                            </Col>
                        )}

                        <Col xs={24} md={8}>
                            <Form.Item
                                name="fundIds"
                                label={isPerFundType(reportType) ? 'Funds (one document each)' : 'Funds'}
                                tooltip={
                                    isPerFundType(reportType)
                                        ? 'A Tax Report is produced per fund, as in the production generate step'
                                        : 'Leave empty for all funds'
                                }
                            >
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder="All funds"
                                    maxTagCount="responsive"
                                    options={FUNDS.map((f) => ({
                                        value: f.fundId,
                                        label: `${f.fundId} — ${f.shortName}`
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item name="periodType" label="Period type" rules={[{ required: true }]}>
                                <Select
                                    options={PERIOD_TYPE_OPTIONS}
                                    onChange={() => form.setFieldsValue({ periodValue: undefined })}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            {periodType === PERIOD_TYPES.CUSTOM ? (
                                <Form.Item
                                    name="customRange"
                                    label="Date range"
                                    rules={[{ required: true, message: 'Pick a range' }]}
                                >
                                    <DatePicker.RangePicker style={{ width: '100%' }} format={ISO} />
                                </Form.Item>
                            ) : (
                                <Form.Item
                                    name="periodValue"
                                    label={PERIOD_VALUE_LABELS[periodType] || 'Period'}
                                    rules={[{ required: true, message: 'Pick a period' }]}
                                >
                                    <Select
                                        placeholder="Select a period"
                                        options={concretePeriods.map((p) => ({
                                            value: p.value,
                                            label: `${p.label}  (${p.startDate} → ${p.endDate})`
                                        }))}
                                    />
                                </Form.Item>
                            )}
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="description" label="Description">
                                <Input placeholder="Optional note recorded with the document" />
                            </Form.Item>
                        </Col>
                    </Row>

                    {needsRecipient(reportType) && (
                        <Row gutter={16}>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="recipientName"
                                    label="Recipient name"
                                    rules={[{ required: true, message: 'Required for a third-party statement' }]}
                                >
                                    <Input placeholder="e.g. Bank of Ceylon" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item name="recipientOrganization" label="Organisation">
                                    <Input placeholder="Optional" />
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    <div className="reports-generate-row">
                        <Button type="primary" htmlType="submit" disabled={!canRun}>
                            Generate
                        </Button>
                    </div>
                </Form>
            </div>

            <div className="panel">
                <h3 className="panel-title">Generated Documents</h3>
                <div className="permission-info-banner reports-info-banner">
                    <ActionIcon name="info" />
                    <span>
                        {generated.length} document(s). Each is a snapshot taken when it was generated,
                        so a later transaction never changes an issued statement. Expand to preview.
                    </span>
                </div>

                <Table
                    className="permission-table reports-table"
                    rowKey="id"
                    size="small"
                    columns={generatedColumns}
                    dataSource={generated}
                    pagination={{ pageSize: 8, hideOnSinglePage: true }}
                    locale={{ emptyText: 'Nothing generated yet' }}
                    scroll={{ x: 'max-content' }}
                    expandable={{
                        expandedRowKeys: expanded,
                        onExpandedRowsChange: setExpanded,
                        expandedRowRender: (report) => (
                            <Table
                                rowKey="key"
                                size="small"
                                columns={report.columns.map((column) => ({
                                    title: column.title,
                                    dataIndex: column.key,
                                    key: column.key,
                                    align: column.align,
                                    render: column.mono
                                        ? (v) => <span className="txn-id">{v}</span>
                                        : (v) => (v === '' || v === undefined ? <Text type="secondary">—</Text> : v)
                                }))}
                                dataSource={report.rows.slice(0, 10)}
                                pagination={false}
                                locale={{ emptyText: 'This document has no rows' }}
                                scroll={{ x: 'max-content' }}
                                footer={
                                    report.rows.length > 10
                                        ? () => (
                                              <Text type="secondary">
                                                  Showing 10 of {report.rows.length} rows — download for the rest
                                              </Text>
                                          )
                                        : undefined
                                }
                            />
                        )
                    }}
                />

                {generated.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                        <Tag color="default">
                            {generated.reduce((sum, r) => sum + r.rowCount, 0)} row(s) across all documents
                        </Tag>
                    </div>
                )}
            </div>
        </>
    );
};

export default ReportsSection;
